/**
 * Units of measure: purchase unit ↔ stock unit.
 *
 * Every item has one stock unit (`items.base_uom`) that inventory, BOMs and
 * production count in. A vendor may sell it in a different unit: webbing
 * strap is stocked in inches but bought by the roll, and one roll is 150 ft.
 * `unitsPerPurchaseUom` (1,800 here) is the only number conversions use; the
 * "150 ft" is what the buyer reads off the quote and is kept for display.
 */

interface UnitDef {
  dimension: 'length' | 'weight' | 'volume' | 'count';
  /** Size in the dimension's reference unit (in, oz, fl oz, each). */
  factor: number;
  label: string;
}

const UNITS: Record<string, UnitDef> = {
  in: { dimension: 'length', factor: 1, label: 'in' },
  ft: { dimension: 'length', factor: 12, label: 'ft' },
  yd: { dimension: 'length', factor: 36, label: 'yd' },
  mm: { dimension: 'length', factor: 1 / 25.4, label: 'mm' },
  cm: { dimension: 'length', factor: 1 / 2.54, label: 'cm' },
  m: { dimension: 'length', factor: 1 / 0.0254, label: 'm' },
  oz: { dimension: 'weight', factor: 1, label: 'oz' },
  lb: { dimension: 'weight', factor: 16, label: 'lb' },
  g: { dimension: 'weight', factor: 1 / 28.349523125, label: 'g' },
  kg: { dimension: 'weight', factor: 1000 / 28.349523125, label: 'kg' },
  floz: { dimension: 'volume', factor: 1, label: 'fl oz' },
  qt: { dimension: 'volume', factor: 32, label: 'qt' },
  gal: { dimension: 'volume', factor: 128, label: 'gal' },
  ml: { dimension: 'volume', factor: 1 / 29.5735295625, label: 'ml' },
  l: { dimension: 'volume', factor: 1000 / 29.5735295625, label: 'l' },
  each: { dimension: 'count', factor: 1, label: 'each' },
  dozen: { dimension: 'count', factor: 12, label: 'dozen' },
};

const ALIASES: Record<string, string> = {
  '"': 'in',
  inch: 'in',
  inches: 'in',
  "'": 'ft',
  foot: 'ft',
  feet: 'ft',
  yard: 'yd',
  yards: 'yd',
  yds: 'yd',
  millimeter: 'mm',
  millimeters: 'mm',
  centimeter: 'cm',
  centimeters: 'cm',
  meter: 'm',
  meters: 'm',
  metre: 'm',
  metres: 'm',
  ounce: 'oz',
  ounces: 'oz',
  lbs: 'lb',
  pound: 'lb',
  pounds: 'lb',
  gram: 'g',
  grams: 'g',
  kilogram: 'kg',
  kilograms: 'kg',
  kgs: 'kg',
  'fl oz': 'floz',
  'fl. oz': 'floz',
  quart: 'qt',
  quarts: 'qt',
  gallon: 'gal',
  gallons: 'gal',
  milliliter: 'ml',
  milliliters: 'ml',
  liter: 'l',
  liters: 'l',
  litre: 'l',
  litres: 'l',
  ea: 'each',
  pc: 'each',
  pcs: 'each',
  piece: 'each',
  pieces: 'each',
  unit: 'each',
  units: 'each',
};

export type UomGroup = 'Length' | 'Count' | 'Packaging' | 'Weight' | 'Volume' | 'Other';

export interface UomOption {
  /** What is stored, e.g. "in". */
  value: string;
  /** What the picker shows, e.g. "in (inches)". */
  label: string;
  group: UomGroup;
}

/** The units every UoM picker offers. */
export const UOM_OPTIONS: ReadonlyArray<UomOption> = [
  { value: 'in', label: 'in (inches)', group: 'Length' },
  { value: 'ft', label: 'ft (feet)', group: 'Length' },
  { value: 'yd', label: 'yd (yards)', group: 'Length' },
  { value: 'mm', label: 'mm (millimeters)', group: 'Length' },
  { value: 'cm', label: 'cm (centimeters)', group: 'Length' },
  { value: 'm', label: 'm (meters)', group: 'Length' },
  { value: 'each', label: 'each', group: 'Count' },
  { value: 'pair', label: 'pair', group: 'Count' },
  { value: 'set', label: 'set', group: 'Count' },
  { value: 'kit', label: 'kit', group: 'Count' },
  { value: 'dozen', label: 'dozen', group: 'Count' },
  { value: 'roll', label: 'roll', group: 'Packaging' },
  { value: 'spool', label: 'spool', group: 'Packaging' },
  { value: 'stick', label: 'stick (bar / rod length)', group: 'Packaging' },
  { value: 'sheet', label: 'sheet', group: 'Packaging' },
  { value: 'bag', label: 'bag', group: 'Packaging' },
  { value: 'box', label: 'box', group: 'Packaging' },
  { value: 'pack', label: 'pack', group: 'Packaging' },
  { value: 'case', label: 'case', group: 'Packaging' },
  { value: 'carton', label: 'carton', group: 'Packaging' },
  { value: 'pallet', label: 'pallet', group: 'Packaging' },
  { value: 'lb', label: 'lbs (pounds)', group: 'Weight' },
  { value: 'oz', label: 'oz (ounces)', group: 'Weight' },
  { value: 'g', label: 'g (grams)', group: 'Weight' },
  { value: 'kg', label: 'kg (kilograms)', group: 'Weight' },
  { value: 'fl oz', label: 'fl oz (fluid ounces)', group: 'Volume' },
  { value: 'ml', label: 'ml (milliliters)', group: 'Volume' },
  { value: 'l', label: 'l (liters)', group: 'Volume' },
  { value: 'qt', label: 'qt (quarts)', group: 'Volume' },
  { value: 'gal', label: 'gal (gallons)', group: 'Volume' },
];

export const UOM_GROUP_ORDER: ReadonlyArray<UomGroup> = [
  'Other',
  'Length',
  'Count',
  'Packaging',
  'Weight',
  'Volume',
];

/**
 * Options for a UoM picker. `convertibleTo` keeps only units that convert
 * to it (a BOM line for an item produced in inches can be entered in ft or
 * cm, not rolls). A saved value that isn't on the list (an older "pcs")
 * is kept as its own option so the picker can still show it.
 */
export function uomChoices(
  current?: string | null,
  convertibleTo?: string | null,
): UomOption[] {
  const target = convertibleTo?.trim();
  let list = [...UOM_OPTIONS];
  if (target) list = list.filter((o) => unitFactor(o.value, target) != null);
  const extras: UomOption[] = [];
  if (target && !list.some((o) => sameUom(o.value, target))) {
    extras.push({ value: target, label: target, group: 'Other' });
  }
  const cur = current?.trim();
  if (cur && ![...extras, ...list].some((o) => sameUom(o.value, cur))) {
    extras.push({ value: cur, label: cur, group: 'Other' });
  }
  return [...extras, ...list];
}

/** The option a stored value selects in `choices` ("lbs" selects "lb"). */
export function matchUomChoice(value: string | null | undefined, choices: ReadonlyArray<UomOption>): string {
  const v = value?.trim() ?? '';
  if (!v) return '';
  return choices.find((o) => o.value === v)?.value ?? choices.find((o) => sameUom(o.value, v))?.value ?? v;
}

function unitKey(uom: string | null | undefined): string | null {
  if (!uom) return null;
  const raw = uom.trim().toLowerCase().replace(/\.$/, '');
  if (!raw) return null;
  if (UNITS[raw]) return raw;
  return ALIASES[raw] ?? null;
}

export function sameUom(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const ka = unitKey(a);
  const kb = unitKey(b);
  if (ka && kb) return ka === kb;
  return a.trim().toLowerCase() === b.trim().toLowerCase();
}

/** How many `to` are in one `from` (1 ft → 12 in), or null when the units don't convert. */
export function unitFactor(from: string | null | undefined, to: string | null | undefined): number | null {
  if (sameUom(from, to)) return 1;
  const a = unitKey(from);
  const b = unitKey(to);
  if (!a || !b) return null;
  const ua = UNITS[a];
  const ub = UNITS[b];
  if (ua.dimension !== ub.dimension) return null;
  return ua.factor / ub.factor;
}

/**
 * A BOM quantity re-stated in the component's UoM Produced, the unit BOM
 * lines are stored and consumed in: 1.5 ft of webbing produced in inches
 * is 18 in. A unit that doesn't convert keeps the number as typed.
 */
export function toProducedQty(
  qty: number,
  uom: string | null | undefined,
  producedUom: string | null | undefined,
): { quantity: number; uom: string } {
  const produced = producedUom?.trim();
  if (!produced) return { quantity: qty, uom: uom?.trim() || 'each' };
  const factor = unitFactor(uom, produced);
  return {
    quantity: factor == null || factor === 1 ? qty : Math.round(qty * factor * 10_000) / 10_000,
    uom: produced,
  };
}

const ABBREVIATIONS = new Set(
  [...Object.values(UNITS).map((u) => u.label), ...Object.keys(ALIASES).filter((a) => a.length <= 3)].map((u) =>
    u.toLowerCase(),
  ),
);

/** "roll" → "rolls", "box" → "boxes"; abbreviations (in, ft, kg, each) stay as they are. */
export function pluralUom(uom: string, qty: number): string {
  const u = uom.trim();
  if (!u || Math.abs(qty) === 1) return u;
  const lower = u.toLowerCase();
  if (ABBREVIATIONS.has(lower) || lower.endsWith('s') || lower === 'each') return u;
  if (/(x|ch|sh|z)$/.test(lower)) return `${u}es`;
  if (/[^aeiou]y$/.test(lower)) return `${u.slice(0, -1)}ies`;
  return `${u}s`;
}

const QTY = new Intl.NumberFormat('en-US', { maximumFractionDigits: 3 });

export function formatQty(qty: number): string {
  return QTY.format(qty);
}

/** "4 rolls", "1,800 in", "1 each". */
export function formatQtyUom(qty: number, uom: string | null | undefined): string {
  const u = (uom ?? '').trim();
  return u ? `${formatQty(qty)} ${pluralUom(u, qty)}` : formatQty(qty);
}

/** Stock units for a quantity in an ordered unit; whole units, like on-hand. */
export function toStockQty(qty: number, unitsPerPurchaseUom: number | null | undefined): number {
  return Math.round(qty * (unitsPerPurchaseUom || 1));
}

/** True when a purchase unit is a real conversion (not just the stock unit again). */
export function isPackUnit(conversion: number | null | undefined): boolean {
  return conversion != null && Number.isFinite(conversion) && Math.abs(conversion - 1) > 1e-9;
}

export interface PurchaseUnit {
  /** roll / box / … ; null = the stock unit. */
  purchaseUom: string | null;
  /** 150 (as the vendor states it). */
  size: number | null;
  /** ft */
  sizeUom: string | null;
  /** Stock units in one purchase unit, e.g. 1800. */
  unitsPerPurchaseUom: number;
}

interface SupplierUnitColumns {
  purchase_uom: string | null;
  purchase_uom_size: number | null;
  purchase_uom_size_uom: string | null;
  units_per_purchase_uom: number | null;
}

export function supplierPurchaseUnit(row: SupplierUnitColumns | null | undefined): PurchaseUnit | null {
  if (!row?.purchase_uom) return null;
  return {
    purchaseUom: row.purchase_uom,
    size: row.purchase_uom_size == null ? null : Number(row.purchase_uom_size),
    sizeUom: row.purchase_uom_size_uom,
    unitsPerPurchaseUom: Number(row.units_per_purchase_uom ?? 1) || 1,
  };
}

/** The purchase unit people think in for an item: the primary supplier's, else any supplier's. */
export function mainPurchaseUnit(
  suppliers: Array<SupplierUnitColumns & { is_primary?: boolean }> | null | undefined,
): PurchaseUnit | null {
  const list = (suppliers ?? []).filter(
    (s) => s.purchase_uom && isPackUnit(Number(s.units_per_purchase_uom ?? 1)),
  );
  return supplierPurchaseUnit(list.find((s) => s.is_primary) ?? list[0]);
}

/** The unit an order is written in: the vendor's purchase unit, else the stock unit. */
export function orderUom(unit: Pick<PurchaseUnit, 'purchaseUom'> | null | undefined, baseUom: string): string {
  return unit?.purchaseUom?.trim() || baseUom;
}

/**
 * "1 roll = 150 ft = 1,800 in". Skips the middle step when the vendor's
 * size is already in the stock unit ("1 box = 100 each").
 */
export function describePurchaseUnit(unit: PurchaseUnit, baseUom: string): string | null {
  const uom = unit.purchaseUom?.trim();
  if (!uom || !isPackUnit(unit.unitsPerPurchaseUom)) return null;
  const stock = formatQtyUom(unit.unitsPerPurchaseUom, baseUom);
  if (unit.size != null && unit.sizeUom && !sameUom(unit.sizeUom, baseUom)) {
    return `1 ${uom} = ${formatQtyUom(unit.size, unit.sizeUom)} = ${stock}`;
  }
  return `1 ${uom} = ${stock}`;
}

/**
 * How a vendor prices a purchase unit when it isn't per purchase unit:
 * Ryerson sells bar stock by the stick but charges by the pound, so a
 * 12 ft stick of 1/4" rod that weighs 2.004 lb is { priceUom: 'lb',
 * perPurchaseUom: 2.004 }. Supplier and PO prices stay stored per stick;
 * this only converts the vendor's quoted rate to and from it.
 */
export interface PricingUnit {
  priceUom: string;
  /** Price units in one purchase unit, e.g. 2.004 lb in one stick. */
  perPurchaseUom: number;
}

interface SupplierPricingColumns {
  purchase_uom: string | null;
  price_uom?: string | null;
  price_uom_per_purchase_uom?: number | string | null;
}

export function supplierPricingUnit(row: SupplierPricingColumns | null | undefined): PricingUnit | null {
  const priceUom = row?.price_uom?.trim();
  const per = Number(row?.price_uom_per_purchase_uom);
  if (!row?.purchase_uom || !priceUom || !Number.isFinite(per) || per <= 0) return null;
  return { priceUom, perPurchaseUom: per };
}

const roundCents = (cents: number) => Math.round(cents * 10_000) / 10_000;

/** Vendor's rate → price per purchase unit: $5.25/lb × 2.004 lb = $10.521 a stick. */
export function toPurchaseUnitCents(centsPerPriceUom: number, pricing: PricingUnit): number {
  return roundCents(centsPerPriceUom * pricing.perPurchaseUom);
}

/** Price per purchase unit → the vendor's rate: $10.521 a stick ÷ 2.004 lb = $5.25/lb. */
export function toPriceUomCents(centsPerPurchaseUnit: number, pricing: PricingUnit): number {
  return roundCents(centsPerPurchaseUnit / pricing.perPurchaseUom);
}

/** "1 stick ≈ 2.004 lb". A weight is approximate: the vendor bills what it actually ships. */
export function describePricingUnit(pricing: PricingUnit, purchaseUom: string): string {
  const sign = unitFactor(pricing.priceUom, 'lb') != null ? '≈' : '=';
  return `1 ${purchaseUom.trim()} ${sign} ${formatQtyUom(pricing.perPurchaseUom, pricing.priceUom)}`;
}

/**
 * Stock units read back in purchase units, so a count of inches is easy
 * to picture: 5,400 in → "3 rolls", 4,000 in → "2 rolls + 400 in".
 */
export function breakdownInPurchaseUnits(
  stockQty: number,
  unitsPerPurchaseUom: number,
  purchaseUom: string,
  baseUom: string,
): string | null {
  if (!isPackUnit(unitsPerPurchaseUom) || unitsPerPurchaseUom <= 0 || !Number.isFinite(stockQty)) return null;
  const sign = stockQty < 0 ? '−' : '';
  const abs = Math.abs(stockQty);
  const whole = Math.floor(abs / unitsPerPurchaseUom + 1e-9);
  const rest = Math.round((abs - whole * unitsPerPurchaseUom) * 1000) / 1000;
  if (whole === 0) return `${sign}${formatQty(abs / unitsPerPurchaseUom)} ${pluralUom(purchaseUom, 0.5)}`;
  if (rest === 0) return `${sign}${formatQtyUom(whole, purchaseUom)}`;
  return `${sign}${formatQtyUom(whole, purchaseUom)} + ${formatQtyUom(rest, baseUom)}`;
}

/** A UoM Produced quantity in the UoM Stocked: 10,797 in → "6 rolls" (to 2 places). */
export function formatInStockedUnit(qty: number, unit: PurchaseUnit | null | undefined): string | null {
  const conv = unit?.unitsPerPurchaseUom;
  const uom = unit?.purchaseUom?.trim();
  if (!uom || !conv || !isPackUnit(conv) || !Number.isFinite(qty)) return null;
  const n = Math.round((qty / conv) * 100) / 100;
  return `${new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(n)} ${pluralUom(uom, n)}`;
}

const PRECISE_USD = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 5,
});

/** Cost per stock unit, which can be a fraction of a cent: 0.125¢ → "$0.00125". */
export function formatUnitCostCents(cents: number | null | undefined): string {
  if (cents == null || !Number.isFinite(cents)) return '—';
  return PRECISE_USD.format(cents / 100);
}
